import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getProductsSerials } from "../../products/functions";
import Loading from "../../atoms/loading";
import Button from "../../atoms/button";
import LoadingOverlay from "../../atoms/loadingOverlay";
import ToastContext from "../../../context/toastProvider";
import {
  downloadBlob,
  saveModelsTags,
  getSerialsByNumbers,
  saveManualTagEmissions,
} from "../functions";
import { buildApplicableGradePayload } from "../../utils/serialGradeFields";
import { isDouradoEligible } from "./partitionByPaper";
import { buildTagsZip } from "./buildTagsZip";
import { formatLocalDate, zipDownloadName } from "./tagZipNames";
import { focusFirstTagErrorField } from "./focusFirstTagError";
import {
  buildPartitionErrorData,
  buildTagsSendData,
  buildTagSavePayload,
  buildManualAuditPayload,
  applySavedTagsToSerials,
  cardFromSerial,
} from "./submitTagsFlow";
import { validateSelectedTags } from "./validateTagsLive";
import { preloadTagFonts, tagFontsReady } from "./preloadTagFonts";
import TagLabelsWorkspace from "./TagLabelsWorkspace";
import TagsSelectionHub from "./TagsSelectionHub";
import ManualTagsWizard from "./ManualTagsWizard";
import { mergeResolvedSerials } from "./resolveTagsSelection";
import "./tagFonts.css";
import { Sentry } from "../../../sentry";

const PRODUCTS_SESSION_KEY = "mgs-tags-products";
const TAGS_SESSION_KEY = "mgs-tags-session";

const readSessionProducts = () => {
  try {
    const raw = sessionStorage.getItem(PRODUCTS_SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch (_err) {
    return null;
  }
};

const writeTagsSession = (payload) => {
  try {
    sessionStorage.setItem(TAGS_SESSION_KEY, JSON.stringify(payload));
  } catch (_err) {
    /* ignore quota */
  }
};

const buildCardsFromSerials = (serialList) =>
  (serialList || []).map((s) => {
    const card = cardFromSerial(
      s,
      buildApplicableGradePayload(s, s?.product || s?.productData)
    );
    if (isDouradoEligible(card)) {
      card.invertida = false;
    }
    return card;
  });

const TagGenerator = ({ user }) => {
  const location = useLocation();
  const { state } = location || {};
  const products = state?.products?.length ? state.products : readSessionProducts();
  const { goodToast, badToast } = useContext(ToastContext);

  const [phase, setPhase] = useState(() => (products?.length ? "generator" : "hub"));
  const [serials, setSerials] = useState(null);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [tagsData, setTagsData] = useState([]);
  const [selection, setSelection] = useState(false);
  const [fontsReady, setFontsReady] = useState(() => tagFontsReady());
  const [unifyPages, setUnifyPages] = useState(false);

  useEffect(() => {
    if (state?.products?.length) {
      try {
        sessionStorage.setItem(PRODUCTS_SESSION_KEY, JSON.stringify(state.products));
      } catch (_err) {
        /* ignore quota */
      }
    }
  }, [state?.products]);

  useEffect(() => {
    let cancelled = false;
    preloadTagFonts()
      .then(() => {
        if (!cancelled) setFontsReady(true);
      })
      .catch(() => {
        /* ignore: font optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (products?.length) {
      doGetProductsSerials();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (phase !== "generator" || !serials?.length) return;
    setTagsData((prev) => {
      const manuals = (prev || []).filter((c) => c?.source === "manual");
      const fromDb = buildCardsFromSerials(serials);
      const manualSerials = new Set(manuals.map((m) => m.serial));
      return [...fromDb.filter((c) => !manualSerials.has(c.serial)), ...manuals];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serials]);

  // Validação em tempo real (largura + partição) antes do download.
  useEffect(() => {
    if (!tagsData?.length) {
      setErrorData(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try {
        const next = validateSelectedTags(tagsData);
        setErrorData(next.cards.length ? next : null);
      } catch (error) {
        const partitionError = buildPartitionErrorData(error);
        setErrorData(partitionError);
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [tagsData, fontsReady]);

  const doGetProductsSerials = async () => {
    if (!products?.length) return;
    setLoading(true);
    const data = await getProductsSerials(user?.token, products);
    if (data?.type === "success") {
      setSerials(data?.data);
    }
    setLoading(false);
  };

  const buildCardForPartition = (serial, tagsEntry) => {
    if (tagsEntry) return tagsEntry;
    return {
      ...buildApplicableGradePayload(serial, serial?.product || serial?.productData),
      serial: serial?.serialNumber,
    };
  };

  const handleContinue = async ({ productIds, serialNumbers }) => {
    setLoading(true);
    let fromProducts = [];
    if (productIds?.length) {
      const data = await getProductsSerials(user?.token, productIds);
      if (data?.type === "success") {
        fromProducts = data?.data || [];
      } else {
        badToast(data?.message || "Erro ao buscar seriais dos pedidos");
        setLoading(false);
        return;
      }
    }

    let fromLookup = { found: [], missing: [] };
    if (serialNumbers?.length) {
      const data = await getSerialsByNumbers(user?.token, serialNumbers);
      if (data?.type === "success") {
        fromLookup = {
          found: data.found || [],
          missing: data.missing || [],
        };
      } else {
        badToast(data?.message || "Erro no lookup de seriais");
        setLoading(false);
        return;
      }
    }

    const { serials: merged, missing: nextMissing } = mergeResolvedSerials({
      fromProducts,
      fromLookup,
    });
    setSerials(merged);
    setMissing(nextMissing);

    const nextPhase = nextMissing.length ? "wizard" : "generator";
    writeTagsSession({
      productIds,
      serialNumbers,
      serials: merged,
      phase: nextPhase,
    });
    setPhase(nextPhase);
    setLoading(false);
  };

  const handleWizardComplete = (manualCards) => {
    const dbCards = buildCardsFromSerials(serials);
    if (!serials?.length && !manualCards?.length) {
      badToast("Nenhum serial selecionado");
      setPhase("hub");
      setSerials(null);
      setMissing([]);
      setTagsData([]);
      return;
    }
    setTagsData([...dbCards, ...(manualCards || [])]);
    setPhase("generator");
    writeTagsSession({
      serials,
      phase: "generator",
      tagsData: [...dbCards, ...(manualCards || [])],
    });
  };

  const handleWizardCancel = () => {
    setPhase("hub");
    setMissing([]);
  };

  const selectedCount = tagsData?.filter((d) => d?.select)?.length || 0;
  const allSelected =
    tagsData?.length > 0 && selectedCount === tagsData.length;
  const hasBlockingErrors = (errorData?.cards?.length || 0) > 0;
  const canDownload = selectedCount > 0 && !hasBlockingErrors;

  const setAllSelect = (value) => {
    setTagsData((prev) => (prev || []).map((card) => ({ ...card, select: value })));
  };

  const enableSelection = () => {
    setAllSelect(false);
    setSelection(true);
  };

  const cancelSelection = () => {
    setAllSelect(true);
    setSelection(false);
  };

  const toggleSelectAll = () => {
    setAllSelect(!allSelected);
  };

  const submitTags = async () => {
    try {
      setGenerationLoading(true);
      const liveErrors = validateSelectedTags(tagsData);
      if (liveErrors.cards.length) {
        setErrorData(liveErrors);
        badToast("Corrija os campos que excedem o limite antes de baixar");
        window.setTimeout(() => focusFirstTagErrorField(liveErrors), 80);
        return;
      }
      setErrorData(null);
      const sendData = buildTagsSendData(tagsData);
      const savePayload = buildTagSavePayload(tagsData, serials);

      if (savePayload.length) {
        const response = await saveModelsTags(user?.token, savePayload);
        if (response?.type === "error") {
          if (response?.data) {
            setErrorData(response.data);
          }
          badToast(response?.message || "Erro ao salvar etiquetas");
          return;
        }
        setSerials((prev) => applySavedTagsToSerials(prev, savePayload));
      }

      const auditPayload = buildManualAuditPayload(tagsData);
      if (auditPayload.length) {
        const manualResponse = await saveManualTagEmissions(user?.token, auditPayload);
        if (manualResponse?.type === "error") {
          badToast(manualResponse?.message || "Erro ao registrar etiquetas manuais");
          return;
        }
      }

      const date = formatLocalDate();
      const { blob } = await buildTagsZip(sendData, { unifyPages, date });
      downloadBlob(blob, zipDownloadName(date));
      goodToast("Etiquetas geradas e salvas!");
    } catch (error) {
      Sentry.captureException(error, { extra: { area: "tags", action: "submitTags" } });
      const partitionError = buildPartitionErrorData(error);
      if (partitionError) {
        setErrorData(partitionError);
        badToast(error?.message || "Erro na geração de etiquetas");
        return;
      }
      badToast(error?.message || "Erro na geração de etiquetas");
    } finally {
      setGenerationLoading(false);
    }
  };

  if (phase === "hub") {
    return (
      <main className="mb-10 relative pb-24">
        <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
        <div className="px-2 mt-4">
          <TagsSelectionHub user={user} onContinue={handleContinue} />
        </div>
        {loading ? (
          <div className="w-fit m-auto mt-4">
            <Loading />
          </div>
        ) : null}
        <LoadingOverlay loading={loading} />
      </main>
    );
  }

  if (phase === "wizard") {
    return (
      <main className="mb-10 relative pb-24">
        <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
        <div className="px-2 mt-4">
          <ManualTagsWizard
            missing={missing}
            onComplete={handleWizardComplete}
            onCancel={handleWizardCancel}
            fontsReady={fontsReady}
          />
        </div>
        <LoadingOverlay loading={loading} />
      </main>
    );
  }

  return (
    <main className="mb-10 relative pb-24">
      <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
      {loading ? (
        <div className="w-fit m-auto mt-4">
          <Loading />
        </div>
      ) : (
        <div className="px-2">
          {selection ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button classes="bg-primary m-0" click={cancelSelection}>
                Cancelar seleção
              </Button>
              <Button classes="bg-primary m-0" click={toggleSelectAll}>
                {allSelected ? "Limpar seleção" : "Selecionar todas"}
              </Button>
            </div>
          ) : (
            <div className="mt-2">
              <Button classes="bg-primary" click={enableSelection}>
                Habilitar seleção
              </Button>
            </div>
          )}
          {!serials?.length && !tagsData?.length ? (
            <p className="text-center">Nenhum serial registrado neste pedido</p>
          ) : (
            <TagLabelsWorkspace
              tagsData={tagsData}
              errorData={errorData}
              serials={serials}
              selection={selection}
              setTagsData={setTagsData}
              fontsReady={fontsReady}
              douradoEligibleFor={(s, tagsEntry) =>
                isDouradoEligible(buildCardForPartition(s, tagsEntry))
              }
            />
          )}
          {tagsData?.length ? (
            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#d59106]/50 bg-[#1a1a1a]/95 backdrop-blur-sm px-3 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.35)]">
              <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
                <Button
                  classes="bg-green-300 hover:bg-green-500 text-xl disabled:opacity-50"
                  disabled={!canDownload}
                  click={submitTags}
                >
                  Baixar ZIP
                </Button>
                <label className="flex items-center gap-2 text-sm text-[#ffedd5]/80 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={unifyPages}
                    onChange={(e) => setUnifyPages(e.target.checked)}
                  />
                  Unificar páginas
                </label>
                <span className="text-sm text-[#ffedd5]/80">
                  {selectedCount} selecionada{selectedCount === 1 ? "" : "s"}
                </span>
                {hasBlockingErrors ? (
                  <span className="text-sm text-red-400">
                    Download bloqueado — corrija os campos com borda vermelha.
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
      <LoadingOverlay loading={generationLoading} />
    </main>
  );
};

export default TagGenerator;
